class BundleBuilder extends HTMLElement {
    constructor() {
        super();
        this.bundleData = JSON.parse(this.querySelector('#bundle-data').innerHTML).sort((a, b) => a.count - b.count);
        this.selectedBundle = this.bundleData[0];
        this.tierFloor = this.bundleData[0].count;
        this.currencySymbol = this.getAttribute('data-currency-symbol');

        this.selectedProducts = [];

        this.initElements();

        this._ensureTierStyles();
        this.loadStateFromStorage();
        this.initializeTabStickyListener();
        this.initializeStickyButtonListener();
        this.initializeTabListeners();
        this.updateUI();
        this.initProductPagination();
        this.initializeProductCardListeners();
        this.initializeProductSliderListeners();
        this.initializePopupCarousel();
        this.initializeBundleATCListener();

        this.addEventListener('bundle:add', this.handleBundleAdd.bind(this));
        this.addEventListener('bundle:remove', this.handleBundleRemove.bind(this));

        // Agar user checkout complete kar ke thank_you / order status page par pahunch chuka hai,
        // to purana bundle state clear kar do taake agli dafa fresh bundle start ho.
        this.clearStateIfOrderCompleted();
    }

    getStorageKey() {
        return `bundleBuilderState:${window.location.pathname}`;
    }

    saveStateToStorage() {
        try {
            const state = {
                selectedBundleCount: this.selectedBundle?.count,
                tierFloor: this.tierFloor,
                selectedProducts: this.selectedProducts || [],
            };
            window.localStorage.setItem(this.getStorageKey(), JSON.stringify(state));
        } catch (e) {
            console.warn('BundleBuilder: unable to save state', e);
        }
    }

    loadStateFromStorage() {
        try {
            const raw = window.localStorage.getItem(this.getStorageKey());
            if (!raw) return;

            const state = JSON.parse(raw);
            if (!state || typeof state !== 'object') return;

            const { selectedBundleCount, selectedProducts, tierFloor } = state;

            if (selectedBundleCount) {
                const matchedBundle = this.bundleData.find(b => b.count === selectedBundleCount);
                if (matchedBundle) {
                    this.selectedBundle = matchedBundle;
                }
            }

            if (Array.isArray(selectedProducts)) {
                this.selectedProducts = selectedProducts
                    .filter(p => p && p.id && p.image && typeof p.price !== 'undefined');
            }
            // The floor is the tile the shopper tapped (older saved states only had the pack): the pack then follows the item count.
            this.tierFloor = this.bundleData.some(b => b.count === tierFloor) ? tierFloor : (this.selectedBundle.count || this.bundleData[0].count);
            this.applyTierForCount();
        } catch (e) {
            console.warn('BundleBuilder: unable to load state', e);
        }
    }

    clearStateFromStorage() {
        try {
            window.localStorage.removeItem(this.getStorageKey());
        } catch (e) {
            console.warn('BundleBuilder: unable to clear state', e);
        }
    }

    // Order complete hone ke baad (thank_you / order status page) state clear karo.
    // Checkout button click par state clear NAHI hoti — is se user checkout se
    // "Back" aaye to uska bundle waisa hi restore ho jata hai jaisa chhoda tha.
    clearStateIfOrderCompleted() {
        try {
            const path = window.location.pathname;
            const isOrderCompletePage = path.includes('/thank_you') || path.includes('/orders/');
            if (isOrderCompletePage) {
                this.clearStateFromStorage();
            }
        } catch (e) {
            console.warn('BundleBuilder: unable to check order completion state', e);
        }
    }

    formatMoney(amount) {
        return window.fzMoney ? fzMoney(amount) : `${this.currencySymbol || ''}${(amount / 100).toFixed(2)}`;
    }

    localizeDollarsToCurrency(amount) {
        return amount * window.Shopify.currency.rate;
    }

    initElements() {
        this.tabsSection = this.querySelector('#tabs-section');
        this.tabs = this.tabsSection.querySelector('.tabs');
        this.tabContents = [...this.querySelectorAll('.tab-content')];
        this.tabButtons = [...this.tabs.querySelectorAll('.tab-btn')];

        this.productSliders = this.querySelectorAll('.product-slider');

        this.bundleOptions = this.querySelector('.bundle-options');
        this.progressBars = this.querySelectorAll('.progress-fill');

        this.progressSavingsAmount = this.querySelector('.progress-savings-amount');
        this.progressStatus = this.querySelector('.progress-status');

        this.bundleData.forEach(bundle => {
            const bundleOption = this.querySelector('#bundle-template').content.cloneNode(true).querySelector('div');
            bundleOption.querySelector('.bundle-count').textContent = bundle.count;
            let price = this.formatMoney(this.localizeDollarsToCurrency(bundle.discount * 100));
            price = price.replace(/\s/g, '');
            bundleOption.querySelector('.bundle-discount').textContent = ((window.fzT && fzT.box_off) || '{amount} off').replace('{amount}', price);

            bundleOption.dataset.count = bundle.count;
            this.bundleOptions.appendChild(bundleOption);

            bundleOption.addEventListener('click', () => {
                this.onBundleChange(bundle.count);
            });
        });

        this.originalPrice = this.querySelector('[data-original-price]');
        this.discountedPrice = this.querySelector('[data-discounted-price]');
        this.discount = this.querySelector('[data-discount]');

        this.bundleATCButton = this.querySelector('.btn_cr:not(.sticky-btn)');
        this.stickyButtonContainer = this.querySelector('.sticky-btn-container');
        this.stickyButton = this.stickyButtonContainer?.querySelector('.sticky-btn');

        this.stickyItemsAdded = this.stickyButtonContainer?.querySelector('.sticky-items-added');
        this.stickyItemsRemaining = this.stickyButtonContainer?.querySelector('.sticky-items-remaining');
        this.stickyProgressFill = this.stickyButtonContainer?.querySelector('.sticky-progress-fill');
        this.stickySavingsAmount = this.stickyButtonContainer?.querySelector('.sticky-savings-amount');
        this.stickyStatus = this.stickyButtonContainer?.querySelector('.sticky-status');
        this.stickyStatusCount = this.stickyButtonContainer?.querySelector('.sticky-status-count');
    }

    initializeBundleATCListener() {
        this.bundleATCButton.addEventListener('click', () => {
            const itemAddStrings = this.selectedProducts.map((product) => `${product.id}:1`);
            const checkoutParamString = `${itemAddStrings.join(',')}?discount=${this.selectedBundle.discount_code}`;
            const permalink = `/cart/${checkoutParamString}`;
            // FIX: clearStateFromStorage() yahan se hata diya gaya hai.
            // Pehle ye yahan call hota tha aur redirect se pehle hi localStorage khali kar deta tha,
            // isliye jab user checkout se "Back" aata tha to bundle empty dikhta tha (products remove ho jate the).
            // Ab state clear sirf order complete hone ke baad hoti hai (clearStateIfOrderCompleted mein),
            // is liye back aane par bundle wapis restore ho jayega.
            window.location.href = permalink;
        });
    }

    updateCardHighlights() {
        this.querySelectorAll('.product-card').forEach(card => {
            const btn = card.querySelector('.add-btn');
            if (!btn) return;
            const vid = btn.dataset.id;
            if (!vid) return;
            const count = this.selectedProducts.filter(p => String(p.id) === String(vid)).length;
            if (count > 0) {
                card.classList.add('in-bundle');
                card.setAttribute('data-bundle-count', count);
            } else {
                card.classList.remove('in-bundle');
                card.removeAttribute('data-bundle-count');
            }
        });
    }

    updateUI() {
        const progressBarWidth = (this.selectedProducts.length / this.selectedBundle.count) * 100;
        this.progressBars.forEach(pb => pb.style.width = `${progressBarWidth}%`);

        const itemsAdded = this.selectedProducts.length;
        const itemsRemaining = this.selectedBundle.count - itemsAdded;
        const isBundleComplete = itemsAdded === this.selectedBundle.count;
        const nextTier = this.bundleData.find(b => b.count > this.selectedBundle.count) || null;
        const isBoxFull = isBundleComplete && !nextTier;
        const upHint = isBundleComplete && nextTier ? `<span class="fz-bb-up">${((window.fzT && fzT.box_up_hint) || 'Add 1 more and you move up to the {pack} ({amount} off)').replace('{pack}', this.packLabel(nextTier)).replace('{amount}', this.discountLabel(nextTier))}</span>` : '';

        if (this.progressSavingsAmount) {
            const savingsAmount = this.localizeDollarsToCurrency(this.selectedBundle.discount * 100);
            this.progressSavingsAmount.textContent = this.formatMoney(savingsAmount);
        }
        if (this.progressStatus) {
            if (isBundleComplete) {
                this.progressStatus.innerHTML = ((window.fzT && fzT.box_complete_ready) || 'Bundle complete! Ready to checkout') + upHint;
                this.progressStatus.classList.add('complete');
            } else {
                const progressPercent = Math.round(progressBarWidth);
                this.progressStatus.innerHTML = ((window.fzT && fzT.box_add_more) || 'Add {n} more to complete your bundle').replace('{n}', `<span class="progress-status-count">${itemsRemaining}</span>`) + ` <span class="clr_discount">(${((window.fzT && fzT.box_pct_done) || '{p}% done').replace('{p}', progressPercent)})</span>`;
                this.progressStatus.classList.remove('complete');
            }
        }

        const placeholderImage = this.querySelector('template[data-placeholder-image]').content.cloneNode(true).querySelector('img');
        const imagesContainer = this.querySelector('.curve-images');

        imagesContainer.innerHTML = '';

        this.selectedProducts.forEach(product => {
            const img = document.createElement('img');
            img.src = product.image;

            const crossButton = document.createElement('button');
            crossButton.classList.add('cross-btn');
            crossButton.innerHTML = '×';
            crossButton.addEventListener('click', () => {
                this.dispatchEvent(new CustomEvent('bundle:remove', {
                    detail: { variantId: product.id }
                }));
            });

            const imgContainer = document.createElement('div');
            imgContainer.classList.add('img-container');
            imgContainer.appendChild(img);
            imgContainer.appendChild(crossButton);

            imagesContainer.appendChild(imgContainer);
        });

        for (let i = this.selectedProducts.length; i < this.selectedBundle.count; i++) {
            imagesContainer.appendChild(placeholderImage.cloneNode(true));
        }

        this.bundleOptions.querySelectorAll('.bundle').forEach(bundle => {
            bundle.classList.remove('active');
        });
        this.bundleOptions.querySelector(`[data-count="${this.selectedBundle.count}"]`).classList.add('active');

        const totalPrice = this.selectedProducts.reduce((acc, product) => acc + +product.price, 0);

        this.discount.textContent = this.formatMoney(this.localizeDollarsToCurrency(this.selectedBundle.discount * 100));

        if (totalPrice === 0) {
            this.originalPrice.textContent = '';
            this.discountedPrice.textContent = '';
        } else {
            this.originalPrice.textContent = this.formatMoney(totalPrice);
            const bundleDiscountLocalized = this.localizeDollarsToCurrency(this.selectedBundle.discount * 100);
            const discountedPrice = totalPrice - bundleDiscountLocalized;
            this.discountedPrice.textContent = this.formatMoney(discountedPrice);
        }

        if (isBundleComplete) {
            this.bundleATCButton.disabled = false;
            this.bundleATCButton.classList.remove('disabled');
            if (this.stickyButton) {
                this.stickyButton.disabled = false;
                this.stickyButton.classList.remove('disabled');
            }
            if (isBoxFull) {
                this.querySelectorAll('.add-btn').forEach(btn => {
                    btn.disabled = true;
                    btn.classList.add('disabled', 'is-full');
                    btn.textContent = ((window.fzT && fzT.box_full) || 'Box is full');
                });
            } else {
                // A full pack that can still grow keeps Add alive: the next item upgrades the pack.
                this.querySelectorAll('.add-btn').forEach(btn => btn.classList.remove('is-full'));
                this.querySelectorAll('.product-card').forEach(productCard => {
                    this.onProductChange(productCard);
                });
            }
        } else {
            this.bundleATCButton.disabled = true;
            this.bundleATCButton.classList.add('disabled');
            if (this.stickyButton) {
                this.stickyButton.disabled = true;
                this.stickyButton.classList.add('disabled');
            }
            this.querySelectorAll('.add-btn').forEach(btn => btn.classList.remove('is-full'));
            this.querySelectorAll('.product-card').forEach(productCard => {
                this.onProductChange(productCard);
            });
        }

        if (this.stickyItemsAdded) {
            this.stickyItemsAdded.textContent = (itemsAdded !== 1 ? ((window.fzT && fzT.box_items_added_other) || '{n} items added') : ((window.fzT && fzT.box_items_added_one) || '{n} item added')).replace('{n}', itemsAdded);
        }
        if (this.stickyItemsRemaining) {
            if (isBundleComplete) {
                this.stickyItemsRemaining.textContent = ((window.fzT && fzT.box_complete) || 'Bundle complete!');
                this.stickyItemsRemaining.classList.add('complete');
            } else {
                this.stickyItemsRemaining.textContent = ((window.fzT && fzT.box_remaining) || '{n} remaining').replace('{n}', itemsRemaining);
                this.stickyItemsRemaining.classList.remove('complete');
            }
        }
        if (this.stickyProgressFill) {
            const progressPercent = (itemsAdded / this.selectedBundle.count) * 100;
            this.stickyProgressFill.style.width = `${progressPercent}%`;
        }

        if (this.stickySavingsAmount) {
            const savingsAmount = this.localizeDollarsToCurrency(this.selectedBundle.discount * 100);
            this.stickySavingsAmount.textContent = this.formatMoney(savingsAmount);
        }
        if (this.stickyStatus) {
            if (isBundleComplete) {
                this.stickyStatus.textContent = ((window.fzT && fzT.box_complete_ready) || 'Bundle complete! Ready to checkout');
                this.stickyStatus.classList.add('complete');
            } else {
                const progressPercent = Math.round((itemsAdded / this.selectedBundle.count) * 100);
                this.stickyStatus.innerHTML = ((window.fzT && fzT.box_add_more) || 'Add {n} more to complete your bundle').replace('{n}', `<span class="sticky-status-count">${itemsRemaining}</span>`) + ` (${((window.fzT && fzT.box_pct_done) || '{p}% done').replace('{p}', progressPercent)})`;
                this.stickyStatus.classList.remove('complete');
            }
        }

        this.updateCardHighlights();
        this.saveStateToStorage();
    }

    onBundleChange(count) {
        this.tierFloor = count;
        this.hideTierToast();
        if (count === this.selectedBundle.count) return;

        this.selectedBundle = this.bundleData.find(bundle => bundle.count === count);

        if (this.selectedProducts.length > count) {
            this.selectedProducts = this.selectedProducts.slice(0, count);
        }

        this.updateUI();
    }

    handleBundleAdd(e) {
        const { variantId, count, image, price } = e.detail;

        const postTotal = this.selectedProducts.length + count;

        if (postTotal > this.maxBundleCount()) {
            this.showTierToast(((window.fzT && fzT.box_toast_full) || 'Your box is full: the <b>{pack}</b> holds {n} items. Remove one to swap.').replace('{pack}', this.packLabel(this.selectedBundle)).replace('{n}', this.maxBundleCount()));
            return;
        }

        for (let i = 0; i < count; i++) {
            this.selectedProducts.push({
                id: variantId,
                image: image,
                price: price
            });
        }

        const upgradedFrom = this.applyTierForCount();

        // Card find karo
        const addedCard = [...this.querySelectorAll('.product-card')].find(card => {
            return card.querySelector('.add-btn')?.dataset.id == variantId;
        });

        if (addedCard) {
            const qty = this.selectedProducts.filter(p => String(p.id) === String(variantId)).length;
            addedCard.classList.add('in-bundle');
            addedCard.setAttribute('data-bundle-count', qty);

            // Card ko us tab ke product-grid mein PEHLE move karo
            const parentGrid = addedCard.closest('.product-grid');
            if (parentGrid && parentGrid.firstChild !== addedCard) {
                parentGrid.prepend(addedCard);
            }
        }

        this.updateUI();

        if (upgradedFrom) {
            const b = this.selectedBundle, remaining = b.count - this.selectedProducts.length;
            this.showTierToast(
                ((window.fzT && fzT.box_toast_up) || 'Moved up to the <b>{pack}</b> · <b>{amount} off</b>.').replace('{pack}', this.packLabel(b)).replace('{amount}', this.discountLabel(b)) + ' ' + (remaining > 0 ? ((window.fzT && fzT.box_toast_add_n) || 'Add {n} more to complete it.').replace('{n}', remaining) : ((window.fzT && fzT.box_complete) || 'Bundle complete!')),
                () => { for (let i = 0; i < count; i++) this.dispatchEvent(new CustomEvent('bundle:remove', { detail: { variantId, silent: true } })); }
            );
        }
    }

    handleBundleRemove(e) {
        const variantId = e.detail.variantId;
        let bundleToRemove = -1;
        for (let i = this.selectedProducts.length - 1; i >= 0; i--) {
            if (this.selectedProducts[i].id === variantId) {
                bundleToRemove = i;
                break;
            }
        }

        if (bundleToRemove !== -1) {
            this.selectedProducts.splice(bundleToRemove, 1);

            const downgradedFrom = this.applyTierForCount();
            if (downgradedFrom && !e.detail.silent) {
                this.showTierToast(((window.fzT && fzT.box_toast_back) || 'Back to the <b>{pack}</b> · <b>{amount} off</b>').replace('{pack}', this.packLabel(this.selectedBundle)).replace('{amount}', this.discountLabel(this.selectedBundle)));
            }

            const removedCard = [...this.querySelectorAll('.product-card')].find(card => {
                return card.querySelector('.add-btn')?.dataset.id == variantId;
            });
            if (removedCard) {
                const remaining = this.selectedProducts.filter(p => String(p.id) === String(variantId)).length;
                if (remaining === 0) {
                    removedCard.classList.remove('in-bundle');
                    removedCard.removeAttribute('data-bundle-count');

                    // Remove ke baad: saare in-bundle cards ko top pe lao
                    const parentGrid = removedCard.closest('.product-grid');
                    if (parentGrid) {
                        const inBundleCards = [...parentGrid.querySelectorAll('.product-card.in-bundle')];
                        inBundleCards.reverse().forEach(card => {
                            parentGrid.prepend(card);
                        });
                    }
                } else {
                    removedCard.setAttribute('data-bundle-count', remaining);
                }
            }

            this.updateUI();
        }
    }

    initializeTabStickyListener() {
        // Poora block jisme tabs + product grid hain — jab yeh viewport se nikal jaye,
        // sticky tabs bhi hat jani chahiye (sirf section ke visible hone tak sticky rahe).
        const sectionEl = this.querySelector('.box-products') || this.tabsSection;

        const updateStickyState = () => {
            const tabsRect = this.tabsSection.getBoundingClientRect();
            const sectionRect = sectionEl.getBoundingClientRect();
            const tabsHeight = this.tabs.offsetHeight;

            // Sticky tab bar viewport top se kitni jagah leti hai (thoda buffer ke sath)
            const stickyBarSpace = tabsHeight + 20;

            // Condition 1: user ne tabs section ko cross kar liya ho (scroll niche ki taraf)
            const scrolledPastTabsStart = tabsRect.top <= 0;

            // Condition 2: section ka bottom abhi bhi sticky bar ke niche visible ho
            // (yani section poora scroll ho ke upar nahi nikla)
            const sectionStillInView = sectionRect.bottom > stickyBarSpace;

            if (scrolledPastTabsStart && sectionStillInView) {
                this.tabs.classList.add('is-sticky');
            } else {
                this.tabs.classList.remove('is-sticky');
            }
        };

        window.addEventListener('scroll', updateStickyState);
        window.addEventListener('resize', updateStickyState);

        // Load hote hi ek dafa check kar lo (agar page already scrolled state mein load ho)
        updateStickyState();
    }

    initializeStickyButtonListener() {
        if (!this.bundleATCButton || !this.stickyButtonContainer || !this.stickyButton) return;

        const syncStickyButtonState = () => {
            if (this.bundleATCButton.disabled) {
                this.stickyButton.disabled = true;
                this.stickyButton.classList.add('disabled');
            } else {
                this.stickyButton.disabled = false;
                this.stickyButton.classList.remove('disabled');
            }
        };

        const updateStickyVisibility = () => {
            const buttonRect = this.bundleATCButton.getBoundingClientRect();

            // Sirf ek condition: original ATC button (section ka top) viewport se upar
            // nikal chuka ho to sticky show ho jaye. Iske baad section khatam ho jaye
            // ya na ho — sticky page ke end tak show rahegi. Sirf wapis scroll karke
            // is button ke top pe aane par (buttonRect.bottom >= 0) hide hogi.
            const scrolledPastButton = buttonRect.bottom < 0;

            if (scrolledPastButton) {
                this.stickyButtonContainer.classList.add('mobile-visible');
                syncStickyButtonState();
            } else {
                this.stickyButtonContainer.classList.remove('mobile-visible');
            }
        };

        this.stickyButton.addEventListener('click', () => {
            this.bundleATCButton.click();
        });

        window.addEventListener('scroll', updateStickyVisibility);
        window.addEventListener('resize', updateStickyVisibility);

        // Load hote hi ek dafa check kar lo (agar page already scrolled state mein load ho)
        updateStickyVisibility();
        syncStickyButtonState();
    }

    initializeTabListeners() {
        this.tabButtons.forEach(button => {
            button.addEventListener('click', () => {
                this.querySelectorAll('.tab-btn, .tab-content').forEach((el) => el.classList.remove('active'));
                button.classList.add('active');

                const activeTab = this.querySelector(`#${button.dataset.tab}`);
                activeTab.classList.add('active');

                const products = activeTab.querySelectorAll('.product-card');
                const loadBtn = activeTab.querySelector('.loadmore-btn');
                const initialVisibleCount = 8;
                this.toggleProductVisibility(products, initialVisibleCount, loadBtn);

                const headerOffset = window.innerWidth < 768 ? 80 : 120;
                const scrollTarget = activeTab.getBoundingClientRect().top + window.pageYOffset - headerOffset - 60;

                window.scrollTo({
                    top: Math.max(0, scrollTarget),
                    behavior: 'smooth',
                });

                this.updateCardHighlights();
            });
        });
    }

    moveSliderTo(slider, index) {
        const track = slider.querySelector('.slider-track');
        track.style.transform = 'translateX(-' + index * 100 + '%)';
    }

    initializeProductSliderListeners() {
        this.productSliders.forEach(slider => {
            const slides = slider.querySelectorAll('.slide');
            const prev = slider.querySelector('.left');
            const next = slider.querySelector('.right');
            if (!slides.length || !prev || !next) return;
            let index = 0;

            next.addEventListener('click', (e) => {
                e.preventDefault();
                index = (index + 1) % slides.length;
                this.moveSliderTo(slider, index);
            });

            prev.addEventListener('click', (e) => {
                e.preventDefault();
                index = (index - 1 + slides.length) % slides.length;
                this.moveSliderTo(slider, index);
            });
        });
    }

    toggleProductVisibility(products, visibleCount, loadBtn) {
        products.forEach((card, index) => {
            if (index < visibleCount) {
                card.classList.add('visible');
            }
        });

        if (visibleCount >= products.length) {
            loadBtn.classList.add('hide');
        }
    }

    initProductPagination() {
        for (const tab of this.tabContents) {
            const products = tab.querySelectorAll('.product-card');

            products.forEach(product => {
                const firstColor = product.querySelector('.color-swatch');
                if (firstColor) {
                    firstColor.classList.add('active');
                }

                const firstSize = product.querySelector('.size-swatch');
                if (firstSize) {
                    firstSize.classList.add('active');
                }

                this.onProductChange(product);
            });

            const loadBtn = tab.querySelector('.loadmore-btn');
            let visibleCount = 8;

            this.toggleProductVisibility(products, visibleCount, loadBtn);

            loadBtn.addEventListener('click', (e) => {
                e.preventDefault();
                visibleCount += 8;
                this.toggleProductVisibility(products, visibleCount, loadBtn);
                this.updateCardHighlights();
            });
        }
    }

    // =====================================================================
    // Count-driven pack size (Sep 2026). The tier follows the number of items in the box: adding a 6th item to a full
    // 5-pack moves the shopper up to the 9-pack, removing it drops them back. The tile they tapped is a floor, never a cap.
    // =====================================================================
    maxBundleCount() {
        return this.bundleData[this.bundleData.length - 1].count;
    }

    tierForCount(count) {
        const floor = this.tierFloor || this.bundleData[0].count;
        const fit = this.bundleData.find(b => b.count >= count);
        const want = Math.max(floor, fit ? fit.count : this.maxBundleCount());
        return this.bundleData.find(b => b.count === want) || this.bundleData[this.bundleData.length - 1];
    }

    // Re-derive the pack from the item count. Returns the previous pack when the tier changed, otherwise null.
    applyTierForCount() {
        const before = this.selectedBundle;
        const tier = this.tierForCount(this.selectedProducts.length);
        if (tier.count === before.count) return null;
        this.selectedBundle = tier;
        return before;
    }

    // Discount already banked: the biggest pack the shopper has completely filled (0 under the first tier).
    earnedDiscount() {
        const n = this.selectedProducts.length;
        let earned = 0;
        this.bundleData.forEach(b => { if (n >= b.count) earned = b.discount; });
        return earned;
    }

    packLabel(bundle) {
        return ((window.fzT && fzT.box_pack_label) || '{n}-pack').replace('{n}', bundle.count);
    }

    discountLabel(bundle) {
        return this.formatMoney(this.localizeDollarsToCurrency(bundle.discount * 100)).replace(/\s/g, '');
    }

    showTierToast(html, undo) {
        this._ensureTierStyles();
        let toast = document.querySelector('.fz-bb-toast');
        if (!toast) { toast = document.createElement('div'); toast.className = 'fz-bb-toast'; document.body.appendChild(toast); }
        clearTimeout(this._toastTimer);
        toast.innerHTML = `<span class="fz-bb-toast__txt">${html}</span>` + (undo ? '<button type="button" class="fz-bb-toast__undo">' + ((window.fzT && fzT.box_undo) || 'Undo') + '</button>' : '') + '<button type="button" class="fz-bb-toast__x" aria-label="' + ((window.fzT && fzT.box_dismiss) || 'Dismiss') + '">&times;</button>';
        const hide = () => { toast.classList.remove('is-on'); };
        toast.querySelector('.fz-bb-toast__x').addEventListener('click', hide);
        const u = toast.querySelector('.fz-bb-toast__undo');
        if (u) u.addEventListener('click', () => { hide(); undo(); });
        requestAnimationFrame(() => toast.classList.add('is-on'));
        this._toastTimer = setTimeout(hide, 7000);
    }

    hideTierToast() {
        const toast = document.querySelector('.fz-bb-toast');
        if (toast) toast.classList.remove('is-on');
        clearTimeout(this._toastTimer);
    }

    _ensureTierStyles() {
        if (document.getElementById('fz-bb-tier-styles')) return;
        const style = document.createElement('style');
        style.id = 'fz-bb-tier-styles';
        style.textContent = [
            '.fz-bb-toast{position:fixed;left:50%;top:96px;transform:translate(-50%,-12px);z-index:2147483000;display:flex;align-items:center;gap:10px;max-width:min(92vw,460px);padding:12px 12px 12px 16px;background:#111;color:#fff;border-radius:12px;box-shadow:0 10px 30px rgba(0,0,0,.25);font-family:inherit;font-size:14px;line-height:1.35;opacity:0;pointer-events:none;transition:opacity .25s ease,transform .25s ease;box-sizing:border-box;}',
            '.fz-bb-toast.is-on{opacity:1;pointer-events:auto;transform:translate(-50%,0);}',
            '.fz-bb-toast b{color:#FF9A5C;font-weight:800;}',
            '.fz-bb-toast__txt{flex:1;}',
            '.fz-bb-toast__undo{flex:none;background:#F74E00;color:#fff;border:0;border-radius:8px;padding:8px 14px;font-weight:800;font-size:13px;cursor:pointer;font-family:inherit;}',
            '.fz-bb-toast__x{flex:none;background:transparent;color:#fff;border:0;font-size:20px;line-height:1;padding:0 2px;cursor:pointer;opacity:.7;}',
            '@media (max-width:749px){.fz-bb-toast{top:auto;bottom:150px;width:calc(100vw - 24px);font-size:13px;}}',
            '.progress-status .fz-bb-up,.sticky-status .fz-bb-up{display:block;font-size:.85em;font-weight:600;opacity:.75;margin-top:2px;}',
            '.add-btn.is-full{cursor:not-allowed;}',
        ].join('\n');
        document.head.appendChild(style);
    }

    onProductChange(productCard) {
        function updateATCButton(atcBtn, variant) {
            if (variant && variant.available) {
                atcBtn.disabled = false;
                atcBtn.classList.remove('disabled');
                atcBtn.textContent = atcBtn.getAttribute('data-available');
                atcBtn.dataset.id = variant.id;
                atcBtn.dataset.price = variant.price;
            } else {
                atcBtn.disabled = true;
                atcBtn.classList.add('disabled');
                atcBtn.textContent = atcBtn.getAttribute('data-out-of-stock');
                atcBtn.dataset.id = '';
                atcBtn.dataset.price = '';
            }
        }

        const activeColorSwatch = productCard.querySelector('.color-swatch.active');
        const activeSizeSwatch = productCard.querySelector('.size-swatch.active');

        const atcBtn = productCard.querySelector('.add-btn');
        const productData = JSON.parse(productCard.querySelector('script[type="application/json"]').innerHTML);

        if (!activeColorSwatch && !activeSizeSwatch) {
            if (productData.variants.length !== 1) {
                atcBtn.disabled = true;
                atcBtn.classList.add('disabled');
                atcBtn.textContent = ((window.fzT && fzT.box_select_options) || 'Select options');
                return;
            } else {
                const variant = productData.variants[0];
                updateATCButton(atcBtn, variant);
                return;
            }
        }

        const colorValue = activeColorSwatch?.dataset?.value || null;
        const colorOptionIndex = activeColorSwatch?.dataset?.optionIndex || '';

        const sizeValue = activeSizeSwatch?.dataset?.value || null;
        const sizeOptionIndex = activeSizeSwatch?.dataset?.optionIndex || '';

        const variant = productData.variants.find(v =>
            (!colorOptionIndex || v[`option${colorOptionIndex}`] === colorValue) &&
            (!sizeOptionIndex || v[`option${sizeOptionIndex}`] === sizeValue)
        );

        const productSlider = productCard.querySelector('.product-slider');

        if (variant) {
            const productCardImages = [...productCard.querySelectorAll('.popup-img')];
            const variantImageIndex = productCardImages.findIndex(img => img.dataset.variants.includes(variant.id));

            if (variantImageIndex !== -1) {
                this.moveSliderTo(productSlider, variantImageIndex);
            }
        }

        updateATCButton(atcBtn, variant);
    }

    initializeProductCardListeners() {
        this.addEventListener('click', (e) => {
            if (e.target.classList.contains('color-swatch')) {
                const group = e.target.closest('.color-swatches');
                group.querySelectorAll('.color-swatch').forEach((s) => s.classList.remove('active'));
                e.target.classList.add('active');
            }

            if (e.target.classList.contains('size-swatch')) {
                const group = e.target.closest('.size-swatches');
                group.querySelectorAll('.size-swatch').forEach((s) => s.classList.remove('active'));
                e.target.classList.add('active');
            }

            if (e.target.classList.contains('plus')) {
                const quantity = e.target.closest('.qty').querySelector('input');
                quantity.value = parseInt(quantity.value) + 1;
            }

            if (e.target.classList.contains('minus')) {
                const productCard = e.target.closest('.product-card');
                const quantity = productCard.querySelector('input[type="number"]');

                let currentQty = parseInt(quantity.value);

                if (currentQty > 0) {
                    quantity.value = currentQty - 1;

                    const variantId = productCard.querySelector('.add-btn').dataset.id;

                    const existsInBundle = this.selectedProducts.some(
                        p => String(p.id) === String(variantId)
                    );

                    if (existsInBundle) {
                        this.dispatchEvent(new CustomEvent('bundle:remove', {
                            detail: { variantId }
                        }));
                    }
                }
            }

            if (e.target.classList.contains('color-swatch') || e.target.classList.contains('size-swatch')) {
                this.onProductChange(e.target.closest('.product-card'));
                this.updateCardHighlights();
            }

            if (e.target.classList.contains('add-btn')) {
                const variantId = e.target.dataset.id;
                const variantPrice = e.target.dataset.price;
                const productCardImages = [...e.target.closest('.product-card').querySelectorAll('.popup-img')];
                const variantImage = productCardImages.find(img => img.dataset.variants.includes(variantId)) || productCardImages[0];

                const quantity = e.target.closest('.product-card').querySelector('input[type="number"]').value;

                if (variantId) {
                    this.dispatchEvent(new CustomEvent('bundle:add', {
                        detail: { variantId: variantId, count: +quantity, image: variantImage.src, price: variantPrice }
                    }));
                }
            }
        });
    }

    initializePopupCarousel(e) {
        let slides = [];
        let current = 0;

        function setupCarouselControls(e) {
            const popup = document.getElementById('global-popup');
            if (!popup || !slides.length) return;

            if (e.target.classList.contains('close-popup')) {
                popup.classList.remove('active');
            }

            if (e.target.classList.contains('next')) {
                slides[current].classList.remove('active');
                current = (current + 1) % slides.length;
                slides[current].classList.add('active');
            }

            if (e.target.classList.contains('prev')) {
                slides[current].classList.remove('active');
                current = (current - 1 + slides.length) % slides.length;
                slides[current].classList.add('active');
            }
        }

        function handlePopupImage(e) {
            const img = e.target.closest('.popup-img');
            if (!img) return;

            const popup = document.getElementById('global-popup');
            const container = popup.querySelector('.popup-images');

            const product = img.closest('.product-card');
            if (!product) return;

            const imgs = product.querySelectorAll('.popup-img');

            container.innerHTML = '';
            slides = [];
            current = 0;

            imgs.forEach((i, index) => {
                const clone = i.cloneNode(true);
                clone.classList.add('popup-slide');

                if (i === img) {
                    clone.classList.add('active');
                    current = index;
                }

                container.appendChild(clone);
                slides.push(clone);
            });

            popup.classList.add('active');
        }

        document.addEventListener('click', (e) => {
            handlePopupImage(e);
            setupCarouselControls(e);
        });
    }
}

customElements.define('bundle-builder', BundleBuilder);
